import React, { useState, useEffect } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  getDoc,
} from "firebase/firestore";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Typography,
  Avatar,
  Button,
  Stack,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { Gauge } from "@mui/x-charts/Gauge";
import {
  ExpandMore,
  CreditCard,
  CreditCardOff,
  CreditScore,
  GppGood,
  GppBad,
  GppMaybe,
  Edit,
  Close,
  Save,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const Users = ({ isAdmin, userRoles = [] }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [kids, setKids] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    displayName: "",
    officialFullName: "",
    blueCardNo: "",
    blueCardExpiry: dayjs(),
    originalBlueCardExpiry: "",
    gender: "",
    dateOfBirth: dayjs(),
    profession: "",
    mobileNo: "",
    catechismExperienceInAustralia: "",
    safeguardingTrainingCompleted: "",
    dateOfSafeguardingTraining: dayjs(),
  });
  const [editUserId, setEditUserId] = useState(null);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);
  const [newRoleToAdd, setNewRoleToAdd] = useState("");

  const allRoles = [
    "teacher",
    "principal",
    "director",
    "volunteer",
    "first-aid",
    "admin",
  ];

  // Determine available roles based on current user permissions
  const getAvailableRoles = () => {
    if (isAdmin) {
      return allRoles; // Admin can assign all roles
    } else if (userRoles.includes("principal")) {
      return ["teacher", "volunteer", "first-aid"]; // Principal can only assign teacher, volunteer, and first-aid
    }
    return []; // Other roles cannot assign roles
  };

  const availableRoles = getAvailableRoles();
  const canEditUser = isAdmin || userRoles.includes("principal");

  const db = getFirestore();

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const userCollection = collection(db, "users");
        const userDocs = await getDocs(userCollection);
        const usersList = userDocs.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [db]);

  useEffect(() => {
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    let filtered = users.filter(
      (user) =>
        user.displayName.toLowerCase().includes(lowercasedSearchTerm) ||
        user.email.toLowerCase().includes(lowercasedSearchTerm),
    );

    if (roleFilter !== "ALL") {
      filtered = filtered.filter((user) => user.roles.includes(roleFilter));
    }

    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, users]);

  const exportStudentsToExcel = async () => {
    try {
      const studentsCollection = collection(db, "students");
      const studentsSnapshot = await getDocs(studentsCollection);
      const studentsData = studentsSnapshot.docs.map((doc) => doc.data());

      const worksheet = XLSX.utils.json_to_sheet(studentsData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Students");

      XLSX.writeFile(workbook, "students.xlsx");
    } catch (error) {
      console.error("Error exporting students to Excel: ", error);
    }
  };

  // helper to convert age to age group string
  const getAgeGroup = (age) => {
    if (age === null || age === undefined) return "";
    if (age >= 18 && age < 25) return "18-25";
    if (age < 30) return "25-30";
    if (age < 35) return "30-35";
    if (age < 40) return "35-40";
    if (age < 45) return "40-45";
    if (age < 50) return "45-50";
    if (age < 55) return "50-55";
    if (age < 60) return "55-60";
    return ">60";
  };

  const exportTeachersToExcel = async () => {
    try {
      // fetch classes so we can determine highest assignment
      const classesSnapshot = await getDocs(collection(db, "classes"));
      const classesData = classesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // helper to compute highest class for a teacher byId
      const gradeOrder = [
        "Prep",
        "Grade 1",
        "Grade 2",
        "Grade 3",
        "Grade 4",
        "Grade 5",
        "Grade 6",
        "Grade 7",
        "Grade 8",
        "Grade 9",
        "Grade 10",
        "Grade 11",
        "Grade 12",
        "Pass Out",
      ];

      const getBaseGrade = (name) => {
        const match = name.match(/^(.*?\d+)/);
        return match ? match[1].trim() : name;
      };

      const getHighestClassForTeacher = (userId) => {
        const assigned = classesData
          .filter((cls) => cls.teachers && cls.teachers.includes(userId))
          .map((cls) => cls.name);
        if (assigned.length === 0) return "";
        assigned.sort(
          (a, b) =>
            gradeOrder.indexOf(getBaseGrade(b)) -
            gradeOrder.indexOf(getBaseGrade(a)),
        );
        // strip any section suffix (e.g. "A" from "Grade 5A") before returning
        return getBaseGrade(assigned[0]);
      };

      const teacherUsers = users.filter((user) =>
        user.roles.some((r) => r === "teacher" || r === "principal"),
      );

      const rows = teacherUsers.map((user) => {
        const highestRole = user.roles.includes("principal")
          ? "principal"
          : "teacher";
        const dob = user.dateOfBirth ? dayjs(user.dateOfBirth) : null;
        const age = dob ? dayjs().diff(dob, "year") : null;
        const ageGroup = getAgeGroup(age);
        return {
          "official full name": user.officialFullName || "",
          "display name": user.displayName || "",
          "highest role": highestRole,
          gender: user.gender || "",
          "age group": ageGroup,
          profession: user.profession || "",
          "mobile number": user.mobileNo || "",
          "e-mail address": user.email || "",
          highestClassAssigned: getHighestClassForTeacher(user.id),
          "Years of experience": user.catechismExperienceInAustralia || "",
          "Safe guarding completed": user.safeguardingTrainingCompleted
            ? "Yes"
            : "No",
          "Date of Safe guarding": user.dateOfSafeguardingTraining || "",
          "Blue card no": user.blueCardNo || "",
          "Blue card expiry date": user.blueCardExpiry || "",
        };
      });

      const headerOrder = [
        "official full name",
        "display name",
        "highest role",
        "gender",
        "age group",
        "profession",
        "mobile number",
        "e-mail address",
        "highestClassAssigned",
        "Years of experience",
        "Safe guarding completed",
        "Date of Safe guarding",
        "Blue card no",
        "Blue card expiry date",
      ];
      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headerOrder });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Teachers");
      XLSX.writeFile(workbook, "teachers.xlsx");
    } catch (error) {
      console.error("Error exporting teachers to Excel: ", error);
    }
  };

  const updateStudentsFromExcel = async (event) => {
    try {
      const file = event.target.files[0];
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const studentsData = XLSX.utils.sheet_to_json(worksheet);

      const studentsCollection = collection(db, "students");
      let updatedCount = 0;

      for (const student of studentsData) {
        const q = query(
          studentsCollection,
          where("regNo", "==", student.regNo),
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Update existing record
          const studentDoc = querySnapshot.docs[0];
          await updateDoc(doc(db, "students", studentDoc.id), student);
          updatedCount++;
        } else {
          // Skip new records, log a warning
          console.warn(
            `No matching student found for RegNo: ${student.regNo} - Skipping update.`,
          );
        }
      }
      alert(`${updatedCount} students updated successfully!`);
    } catch (error) {
      console.error("Error updating students from Excel: ", error);
    }
  };

  const updateUserRoles = async (userId, newRoles) => {
    if (!isAdmin && !userRoles.includes("principal")) {
      alert("You do not have permission to modify roles.");
      return;
    }
    try {
      await updateDoc(doc(db, "users", userId), { roles: newRoles });
      alert("Roles updated successfully!");
      // Update local state to reflect changes
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === userId ? { ...user, roles: newRoles } : user,
        ),
      );
    } catch (error) {
      console.error("Error updating roles:", error);
      alert("Failed to update roles. Please try again.");
    }
  };

  const handleExpandClick = async (userId) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setExpandedUserId(userId);

    // Fetch the parent email from the users collection
    const userDocRef = doc(db, "users", userId);
    const userDoc = await getDoc(userDocRef);
    let userEmail = "";
    if (userDoc.exists()) {
      userEmail = userDoc.data().email;
    }

    // Query the students collection using parentEmails array
    const q = query(
      collection(db, "students"),
      where("guardianEmail", "==", userEmail),
    );
    const querySnapshot = await getDocs(q);
    const kidsData = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    setKids((prevKids) => ({ ...prevKids, [userId]: kidsData }));
  };

  const runMigrationRoutine = async () => {
    try {
      const studentsCollection = collection(db, "students");
      const studentsSnapshot = await getDocs(studentsCollection);
      const updatePromises = studentsSnapshot.docs.map((studentDoc) => {
        const studentData = studentDoc.data();
        const updates = {};

        // Add "mediaConsent" field with value = true if it doesn't exist
        if (studentData.mediaConsent === undefined) {
          updates.mediaConsent = true;
        }

        if (studentData.status && studentData.status !== "Active") {
          updates.class = "";
          updates.classYear = null;
        }

        if (Object.keys(updates).length > 0) {
          const studentRef = doc(db, "students", studentDoc.id);
          return updateDoc(studentRef, updates);
        }

        return Promise.resolve();
      });

      await Promise.all(updatePromises);
      alert("Migration routine completed successfully!");
    } catch (error) {
      console.error("Error running migration routine: ", error);
      alert("Failed to run migration routine.");
    }
  };

  const handleEditClick = (user) => {
    setEditFormData({
      displayName: user.displayName || "",
      officialFullName: user.officialFullName || "",
      blueCardNo: user.blueCardNo || "",
      blueCardExpiry: user.blueCardExpiry
        ? dayjs(user.blueCardExpiry)
        : dayjs(),
      originalBlueCardExpiry: user.blueCardExpiry || "",
      gender: user.gender || "",
      dateOfBirth: user.dateOfBirth ? dayjs(user.dateOfBirth) : dayjs(),
      profession: user.profession || "",
      mobileNo: user.mobileNo || "",
      catechismExperienceInAustralia: user.catechismExperienceInAustralia || "",
      safeguardingTrainingCompleted: user.safeguardingTrainingCompleted || "",
      dateOfSafeguardingTraining: user.dateOfSafeguardingTraining
        ? dayjs(user.dateOfSafeguardingTraining)
        : dayjs(),
    });
    setEditUserId(user.id);
    setEditDialogOpen(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditDateChange = (name, value) => {
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleEditSave = async () => {
    if (!canEditUser) {
      alert("You do not have permission to edit user details.");
      return;
    }
    try {
      const userRef = doc(db, "users", editUserId);
      await updateDoc(userRef, {
        displayName: editFormData.displayName,
        officialFullName: editFormData.officialFullName,
        blueCardNo: editFormData.blueCardNo,
        blueCardExpiry: editFormData.blueCardExpiry.format("YYYY-MM-DD"),
        ...(editFormData.blueCardExpiry.format("YYYY-MM-DD") !==
          editFormData.originalBlueCardExpiry && {
          blueCardReminderSent: false,
          blueCardSecondReminderSent: false,
        }),
        gender: editFormData.gender,
        dateOfBirth: editFormData.dateOfBirth.format("YYYY-MM-DD"),
        profession: editFormData.profession,
        mobileNo: editFormData.mobileNo,
        catechismExperienceInAustralia:
          Number(editFormData.catechismExperienceInAustralia) || 0,
        safeguardingTrainingCompleted:
          editFormData.safeguardingTrainingCompleted,
        dateOfSafeguardingTraining:
          editFormData.dateOfSafeguardingTraining.format("YYYY-MM-DD"),
      });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === editUserId
            ? {
                ...user,
                displayName: editFormData.displayName,
                officialFullName: editFormData.officialFullName,
                blueCardNo: editFormData.blueCardNo,
                blueCardExpiry:
                  editFormData.blueCardExpiry.format("YYYY-MM-DD"),
                gender: editFormData.gender,
                dateOfBirth: editFormData.dateOfBirth.format("YYYY-MM-DD"),
                profession: editFormData.profession,
                mobileNo: editFormData.mobileNo,
                catechismExperienceInAustralia:
                  Number(editFormData.catechismExperienceInAustralia) || 0,
                safeguardingTrainingCompleted:
                  editFormData.safeguardingTrainingCompleted,
                dateOfSafeguardingTraining:
                  editFormData.dateOfSafeguardingTraining.format("YYYY-MM-DD"),
              }
            : user,
        ),
      );
      setEditDialogOpen(false);
      alert("User updated successfully!");
    } catch (error) {
      console.error("Error updating user: ", error);
      alert("Failed to update user. Please try again.");
    }
  };

  const teachersCount = users.filter((user) =>
    user.roles.includes("teacher"),
  ).length;

  // volunteer counter
  const volunteersCount = users.filter((user) =>
    user.roles.includes("volunteer"),
  ).length;

  // active kids statistics
  const [activeKidsCount, setActiveKidsCount] = useState(0);
  const [boysCount, setBoysCount] = useState(0);
  const [girlsCount, setGirlsCount] = useState(0);

  useEffect(() => {
    const fetchActiveKids = async () => {
      try {
        const studentsCollection = collection(db, "students");
        const q = query(studentsCollection, where("status", "==", "Active"));
        const studentsSnapshot = await getDocs(q);
        let boys = 0;
        let girls = 0;
        studentsSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.gender === "Male") {
            boys += 1;
          } else if (data.gender === "Female") {
            girls += 1;
          }
        });
        setBoysCount(boys);
        setGirlsCount(girls);
        setActiveKidsCount(studentsSnapshot.size);
      } catch (error) {
        console.error("Error fetching active kids counts:", error);
      }
    };

    fetchActiveKids();
  }, [db]);

  if (loading) {
    return <div>Loading users...</div>;
  }

  return (
    <div>
      <Typography align="center" gutterBottom>
        Registered Users
      </Typography>
      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        spacing={2}
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            id="search-textfield"
            label="Search by Name/Email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            margin="normal"
          />
          <FormControl margin="normal">
            <InputLabel id="role-filter-label">Role</InputLabel>
            <Select
              id="role-filter-select"
              labelId="role-filter-label"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="ALL">ALL</MenuItem>
              <MenuItem value="teacher">Teacher</MenuItem>
              <MenuItem value="principal">Principal</MenuItem>
              <MenuItem value="director">Director</MenuItem>
              <MenuItem value="volunteer">Volunteer</MenuItem>
              <MenuItem value="first-aid">First-aid</MenuItem>
              {isAdmin && <MenuItem value="admin">Admin</MenuItem>}
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          <Stack direction="column" alignItems="center">
            <Typography>Users</Typography>
            <Gauge
              id="gauge-users"
              width={70}
              height={70}
              value={users.length}
            />
          </Stack>
          <Stack direction="column" alignItems="center">
            <Typography>Teachers</Typography>
            <Gauge
              id="gauge-teachers"
              width={70}
              height={70}
              value={teachersCount}
            />
          </Stack>
          <Stack direction="column" alignItems="center">
            <Typography>Volunteers</Typography>
            <Gauge
              id="gauge-volunteers"
              width={70}
              height={70}
              value={volunteersCount}
            />
          </Stack>
          <Stack direction="column" alignItems="center">
            <Tooltip
              title={`Boys: ${boysCount}, Girls: ${girlsCount}`}
              placement="top"
            >
              <div>
                <Typography>Kids</Typography>
                <Gauge
                  id="gauge-kids"
                  width={70}
                  height={70}
                  value={activeKidsCount}
                />
              </div>
            </Tooltip>
          </Stack>
        </Stack>
      </Stack>
      {(isAdmin || userRoles.includes("principal")) && (
        <Button
          id="export-students-to-excel-button"
          variant="contained"
          color="primary"
          onClick={exportStudentsToExcel}
          style={{ marginRight: 8 }}
        >
          Export Students to Excel
        </Button>
      )}
      {(isAdmin || userRoles.includes("principal")) && (
        <Button
          id="export-teachers-to-excel-button"
          variant="contained"
          color="primary"
          onClick={exportTeachersToExcel}
        >
          Export Teachers to Excel
        </Button>
      )}
      {isAdmin && (
        <>
          <input
            accept=".xlsx, .xls"
            style={{ display: "none" }}
            id="update-students-from-excel"
            type="file"
            onChange={updateStudentsFromExcel}
          />
          <label htmlFor="update-students-from-excel">
            <Button
              id="update-students-from-excel-button"
              variant="contained"
              color="secondary"
              component="span"
            >
              Update students from Excel
            </Button>
          </label>
          <Button
            id="run-migration-routine-button"
            variant="contained"
            color="secondary"
            onClick={runMigrationRoutine}
          >
            Run Migration Routine
          </Button>
        </>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Kids
              </TableCell>
              <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Name
              </TableCell>
              <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Email
              </TableCell>
              <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Roles
              </TableCell>
              {canEditUser && (
                <TableCell style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  Edit
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <React.Fragment key={user.id}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      id={`user-expand-button-${user.id}`}
                      onClick={() => handleExpandClick(user.id)}
                    >
                      <ExpandMore />
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {user.displayName || "No Name"}
                    {user.blueCardExpiry &&
                      (user.blueCardExpiry < dayjs().format("YYYY-MM-DD") ? (
                        <Tooltip
                          title={
                            "Blue Card " +
                            user.blueCardNo +
                            " expired on " +
                            dayjs(user.blueCardExpiry).format("DD-MM-YYYY")
                          }
                        >
                          <CreditCardOff
                            style={{ color: "red", marginLeft: 4 }}
                          />
                        </Tooltip>
                      ) : user.blueCardExpiry <
                        dayjs().add(30, "day").format("YYYY-MM-DD") ? (
                        <Tooltip
                          title={
                            "Blue Card " +
                            user.blueCardNo +
                            " expiring soon " +
                            dayjs(user.blueCardExpiry).format("DD-MM-YYYY")
                          }
                        >
                          <CreditCard
                            style={{ color: "orange", marginLeft: 4 }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={
                            "Blue Card " +
                            user.blueCardNo +
                            " valid till " +
                            dayjs(user.blueCardExpiry).format("DD-MM-YYYY")
                          }
                        >
                          <CreditScore
                            style={{ color: "blue", marginLeft: 4 }}
                          />
                        </Tooltip>
                      ))}
                    {user.safeguardingTrainingCompleted === "No" ? (
                      <Tooltip title="Safeguarding Training Not Completed">
                        <GppBad style={{ color: "red", marginLeft: 4 }} />
                      </Tooltip>
                    ) : user.dateOfSafeguardingTraining ? (
                      user.dateOfSafeguardingTraining >=
                      dayjs().subtract(2, "year").format("YYYY-MM-DD") ? (
                        <Tooltip
                          title={`Safeguarding Training Completed: ${dayjs(user.dateOfSafeguardingTraining).format("DD-MM-YYYY")}`}
                        >
                          <GppGood style={{ color: "blue", marginLeft: 4 }} />
                        </Tooltip>
                      ) : user.dateOfSafeguardingTraining >=
                        dayjs().subtract(3, "year").format("YYYY-MM-DD") ? (
                        <Tooltip
                          title={`Safeguarding Training Completed: ${dayjs(user.dateOfSafeguardingTraining).format("DD-MM-YYYY")}`}
                        >
                          <GppMaybe
                            style={{ color: "orange", marginLeft: 4 }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip
                          title={`Safeguarding Training Completed: ${dayjs(user.dateOfSafeguardingTraining).format("DD-MM-YYYY")}`}
                        >
                          <GppMaybe style={{ color: "red", marginLeft: 4 }} />
                        </Tooltip>
                      )
                    ) : null}
                    {user.photoURL && (
                      <Avatar src={user.photoURL} alt="Profile Picture" />
                    )}
                  </TableCell>
                  <TableCell>{user.email || "N/A"}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role) => {
                          // Hide parent role and admin role from non-admin users
                          if (role === "parent") {
                            return null;
                          }
                          if (role === "admin" && !isAdmin) {
                            return null;
                          }
                          // Check if current user can manage this role
                          const canManageRole =
                            isAdmin ||
                            (userRoles.includes("principal") &&
                              (role === "teacher" ||
                                role === "volunteer" ||
                                role === "first-aid"));
                          return (
                            <Chip
                              key={role}
                              label={
                                role.charAt(0).toUpperCase() + role.slice(1)
                              }
                              onClick={() => {
                                if (canManageRole) {
                                  setSelectedUserForRoles(user);
                                  setRoleDialogOpen(true);
                                }
                              }}
                              color="primary"
                              variant="outlined"
                              style={{
                                cursor: canManageRole ? "pointer" : "default",
                                opacity: canManageRole ? 1 : 0.6,
                              }}
                            />
                          );
                        })
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No roles
                        </Typography>
                      )}
                      {availableRoles.length > 0 && (
                        <Button
                          id={`add-role-button-${user.id}`}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setSelectedUserForRoles(user);
                            setRoleDialogOpen(true);
                          }}
                        >
                          + Add Role
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                  {canEditUser && (
                    <TableCell>
                      <IconButton
                        id={`edit-user-button-${user.id}`}
                        onClick={() => handleEditClick(user)}
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={canEditUser ? 5 : 4}
                    style={{ paddingBottom: 0, paddingTop: 0 }}
                  >
                    <Collapse
                      in={expandedUserId === user.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box margin={1}>
                        <Typography variant="h6" gutterBottom component="div">
                          Kids
                        </Typography>
                        {kids[user.id] ? (
                          <Table size="small" aria-label="kids">
                            <TableHead>
                              <TableRow>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Reg No
                                </TableCell>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  First Name
                                </TableCell>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Middle Name
                                </TableCell>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Last Name
                                </TableCell>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Grade
                                </TableCell>
                                <TableCell
                                  style={{
                                    fontWeight: "bold",
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  Class
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {kids[user.id].map((kid) => (
                                <TableRow id={`kid-${kid.id}`} key={kid.id}>
                                  <TableCell>{kid.regNo}</TableCell>
                                  <TableCell>{kid.firstName}</TableCell>
                                  <TableCell>{kid.middleName}</TableCell>
                                  <TableCell>{kid.lastName}</TableCell>
                                  <TableCell>{kid.schoolGrade}</TableCell>
                                  <TableCell>{kid.class}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography>No kids found</Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Role Management Dialog */}
      {availableRoles.length > 0 && (
        <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)}>
          <DialogTitle>
            Manage Roles for {selectedUserForRoles?.displayName}
            <IconButton
              aria-label="close"
              onClick={() => setRoleDialogOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ marginTop: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Roles:
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 3 }}>
                {selectedUserForRoles?.roles &&
                selectedUserForRoles.roles.length > 0 ? (
                  selectedUserForRoles.roles.map((role) => {
                    // Hide parent role and admin role from non-admin users
                    if (role === "parent") {
                      return null;
                    }
                    if (role === "admin" && !isAdmin) {
                      return null;
                    }
                    // Check if current user can remove this role
                    const canRemoveRole =
                      isAdmin ||
                      (userRoles.includes("principal") &&
                        (role === "teacher" ||
                          role === "volunteer" ||
                          role === "first-aid"));
                    return (
                      <Chip
                        key={role}
                        id={`current-role-chip-${role}`}
                        label={role.charAt(0).toUpperCase() + role.slice(1)}
                        onDelete={
                          canRemoveRole
                            ? () => {
                                const updatedRoles =
                                  selectedUserForRoles.roles.filter(
                                    (r) => r !== role,
                                  );
                                updateUserRoles(
                                  selectedUserForRoles.id,
                                  updatedRoles,
                                );
                                setSelectedUserForRoles({
                                  ...selectedUserForRoles,
                                  roles: updatedRoles,
                                });
                              }
                            : undefined
                        }
                        color="primary"
                        style={{
                          opacity: canRemoveRole ? 1 : 0.6,
                        }}
                      />
                    );
                  })
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No roles assigned
                  </Typography>
                )}
              </Stack>

              <Typography variant="subtitle2" gutterBottom>
                Add New Role:
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="add-role-label">Select Role</InputLabel>
                <Select
                  id="add-role-select"
                  labelId="add-role-label"
                  value={newRoleToAdd}
                  onChange={(e) => setNewRoleToAdd(e.target.value)}
                  label="Select Role"
                >
                  {availableRoles.map((role) => (
                    <MenuItem
                      key={role}
                      value={role}
                      disabled={selectedUserForRoles?.roles?.includes(role)}
                    >
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRoleDialogOpen(false)} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newRoleToAdd && selectedUserForRoles) {
                  const updatedRoles = [
                    ...selectedUserForRoles.roles,
                    newRoleToAdd,
                  ];
                  updateUserRoles(selectedUserForRoles.id, updatedRoles);
                  setSelectedUserForRoles({
                    ...selectedUserForRoles,
                    roles: updatedRoles,
                  });
                  setNewRoleToAdd("");
                }
              }}
              color="primary"
              disabled={!newRoleToAdd}
            >
              Add Role
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              mt: 1,
            }}
          >
            <TextField
              margin="dense"
              label="Display Name"
              name="displayName"
              value={editFormData.displayName}
              onChange={handleEditFormChange}
              fullWidth
              sx={{ gridColumn: "1 / -1" }}
            />
            <TextField
              margin="dense"
              label="Official Full Name"
              name="officialFullName"
              value={editFormData.officialFullName}
              onChange={handleEditFormChange}
              fullWidth
              sx={{ gridColumn: "1 / -1" }}
            />
            <TextField
              margin="dense"
              label="Blue Card Number"
              name="blueCardNo"
              value={editFormData.blueCardNo}
              onChange={handleEditFormChange}
              fullWidth
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Blue Card Expiry"
                value={editFormData.blueCardExpiry}
                onChange={(newValue) =>
                  handleEditDateChange("blueCardExpiry", newValue)
                }
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "dense",
                  },
                }}
              />
            </LocalizationProvider>
            <FormControl fullWidth margin="dense">
              <InputLabel id="edit-gender-label">Gender</InputLabel>
              <Select
                labelId="edit-gender-label"
                label="Gender"
                name="gender"
                value={editFormData.gender}
                onChange={handleEditFormChange}
              >
                <MenuItem value=""></MenuItem>
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date of Birth"
                value={editFormData.dateOfBirth}
                onChange={(newValue) =>
                  handleEditDateChange("dateOfBirth", newValue)
                }
                format="DD-MM-YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: "dense",
                  },
                }}
              />
            </LocalizationProvider>
            <TextField
              margin="dense"
              label="Profession"
              name="profession"
              value={editFormData.profession}
              onChange={handleEditFormChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Mobile No"
              name="mobileNo"
              value={editFormData.mobileNo}
              onChange={handleEditFormChange}
              fullWidth
            />
            <TextField
              margin="dense"
              label="Catechism Experience In Australia (Years)"
              name="catechismExperienceInAustralia"
              type="number"
              value={editFormData.catechismExperienceInAustralia}
              onChange={handleEditFormChange}
              fullWidth
            />
            <FormControl fullWidth margin="dense">
              <InputLabel id="edit-safeguarding-label">
                Safeguarding Training Completed
              </InputLabel>
              <Select
                labelId="edit-safeguarding-label"
                label="Safeguarding Training Completed"
                name="safeguardingTrainingCompleted"
                value={editFormData.safeguardingTrainingCompleted}
                onChange={handleEditFormChange}
              >
                <MenuItem value=""></MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
            {editFormData.safeguardingTrainingCompleted === "Yes" && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Date of Safeguarding Training"
                  value={editFormData.dateOfSafeguardingTraining}
                  onChange={(newValue) =>
                    handleEditDateChange("dateOfSafeguardingTraining", newValue)
                  }
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "dense",
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            color="primary"
            variant="contained"
            startIcon={<Save />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Users;
